<script lang="ts">
  import { aiFeaturesStore } from '$lib/stores/aiFeatures'
  import { TextSegmentService } from '$lib/services/TextSegmentService'
  import { AIService } from '$lib/services/AIService'
  import type { Segment } from '$lib/stores/aiFeatures'

  /**
   * SegmentProcessorController: Handles AI processing of text segments
   * Single Responsibility: Process segments with AI (paraphrase, expand, shrink)
   */

  interface Props {
    originalText: string
    mode: 'paraphrase' | 'expand' | 'shrink'
    segments: Segment[]
    isProcessing: boolean
    onSegmentsChange?: (segments: Segment[]) => void
  }

  let { 
    originalText, 
    mode = 'paraphrase', 
    segments = $bindable([]),
    isProcessing = $bindable(false),
    onSegmentsChange
  }: Props = $props()

  let hasStartedProcessing = false

  // Initialize and process segments when needed
  export async function initializeSegments(propsSegments?: Segment[]) {
    
    if (!propsSegments) {
      // If no props segments and we have original text, create initial segments
      if (originalText && segments.length === 0) {
        const initialSegments = TextSegmentService.segmentText(originalText)
        segments = initialSegments
        
        if (!hasStartedProcessing && !isProcessing) {
          hasStartedProcessing = true
          await processSegments(initialSegments)
        }
      }
    } else if (propsSegments.length > 0) {
      // Update local segments from props
      segments = propsSegments
      
      // Process segments if they don't have suggestions yet and we haven't started processing
      const needsProcessing = segments.some(seg => !seg.suggested || seg.suggested === '')
      
      if (needsProcessing && !hasStartedProcessing && !isProcessing) {
        hasStartedProcessing = true
        await processSegments(segments)
      }
    }
  }

  // Process segments with AI
  export async function processSegments(segs: Segment[]) {
    isProcessing = true
    aiFeaturesStore.setProcessing(true)
    
    try {
      // Process segments sequentially for streaming-like experience
      const processedSegments: Segment[] = []
      
      // Initialize segments with processing state (preserve existing history)
      const initialSegments = segs.map(seg => ({
        ...seg,
        isProcessing: true,
        suggested: '',
        hasChanges: false,
        status: 'pending' as const,
        selected: false,
        history: seg.history || [],
        historyIndex: seg.historyIndex ?? -1
      }))
      
      segments = initialSegments
      aiFeaturesStore.setSegments(segments)
      onSegmentsChange?.(segments)
      
      // Process each segment sequentially with streaming
      for (let i = 0; i < segs.length; i++) {
        const seg = segs[i]
        
        // Mark current segment as processing
        segments[i] = { ...segments[i], isProcessing: true, suggested: '' }
        aiFeaturesStore.setSegments([...segments])
        onSegmentsChange?.([...segments])
        
        let response
        let reason = ''
        let accumulatedText = ''
        let isJsonResponse = false
        
        // Streaming callback to update segment in real-time
        const onChunk = (chunk: string) => {
          // Check if this is JSON response
          if (!isJsonResponse && chunk.startsWith('{')) {
            isJsonResponse = true
          }
          
          if (isJsonResponse) {
            // For JSON responses, accumulate until complete
            accumulatedText += chunk
            
            // Try to parse JSON to extract improved text
            try {
              const parsed = JSON.parse(accumulatedText)
              if (parsed.improved || parsed.text) {
                const extractedText = parsed.improved || parsed.text
                const suggested = TextSegmentService.preserveParagraphStructure(extractedText, seg.paragraphBreaks)
                segments[i] = {
                  ...segments[i],
                  suggested,
                  reason: parsed.reason || '',
                  isProcessing: true
                }
                // Use requestAnimationFrame for smoother updates
                requestAnimationFrame(() => {
                  aiFeaturesStore.setSegments([...segments])
                  onSegmentsChange?.([...segments])
                })
                return // Don't process further once we have the complete JSON
              }
            } catch {
              // JSON not complete yet, continue accumulating
              return
            }
          } else {
            // Regular streaming text
            accumulatedText += chunk
            const suggested = TextSegmentService.preserveParagraphStructure(accumulatedText, seg.paragraphBreaks)
            
            segments[i] = {
              ...segments[i],
              suggested,
              isProcessing: true
            }
            
            // Use requestAnimationFrame for smoother updates
            requestAnimationFrame(() => {
              aiFeaturesStore.setSegments([...segments])
              onSegmentsChange?.([...segments])
            })
          }
        }
        
        if (mode === 'expand') {
          response = await AIService.expandStreaming(seg.original, onChunk)
          reason = response.reason || 
            (response.success && response.result !== seg.original 
              ? '세부 설명을 추가하여 내용을 보강했습니다' 
              : '')
        } else if (mode === 'shrink') {
          response = await AIService.shrinkStreaming(seg.original, onChunk)
          reason = response.reason ||
            (response.success && response.result !== seg.original
              ? '핵심 내용만 남기고 간결하게 정리했습니다'
              : '')
        } else {
          // Default to paraphrase
          response = await AIService.paraphraseStreaming(seg.original, onChunk)
          reason = response.reason || 
            (response.success && response.result !== seg.original
              ? '문장을 더 자연스럽게 개선했습니다'
              : '')
        }
        
        const rawSuggested = response.success ? (response.result as string) : seg.original
        const suggested = TextSegmentService.preserveParagraphStructure(rawSuggested, seg.paragraphBreaks)
        const hasChanges = response.success && suggested !== seg.original
        
        // Update segment with final result
        // Use the reason from segments if it was already set by JSON parsing
        const finalReason = segments[i].reason || reason
        
        segments[i] = {
          ...segments[i],
          suggested,
          reason: finalReason,
          hasChanges,
          mode,
          status: hasChanges ? 'pending' : 'skipped',
          selected: false,
          isProcessing: false,
          // Don't overwrite history here - let the parent handle it
          history: segments[i].history,
          historyIndex: segments[i].historyIndex
        }
        
        processedSegments.push(segments[i])
        
        // Update UI with final segment state
        aiFeaturesStore.setSegments([...segments])
        onSegmentsChange?.([...segments])
        
        // Small delay between segments for visual effect
        if (i < segs.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 50))
        }
      }
      
      segments = TextSegmentService.detectChanges(processedSegments)
      
      aiFeaturesStore.setSegments(segments)
      onSegmentsChange?.(segments)
    } catch (error) {
      aiFeaturesStore.setError('AI 처리 중 오류가 발생했습니다')
    } finally {
      isProcessing = false
      aiFeaturesStore.setProcessing(false)
    }
  }

  export function resetProcessingState() {
    hasStartedProcessing = false
  }
</script>